import { useEffect, useState } from "react";
import { socialPostRenderPayloadSchema, type SocialPostRenderPayload } from "@runcheetah/shared";

import { SocialPostPreview } from "@/components/campaign/social-post-preview";

type RenderWindow = Window &
  typeof globalThis & {
    __runcheetahSocialRenderReady?: boolean;
    __runcheetahSocialRenderError?: string;
    __runcheetahSocialRenderResult?: {
      width: number;
      height: number;
      previewSource: SocialPostRenderPayload["previewSource"];
      generationTaskId: string | null;
      slideId: string | null;
      slideIndex: number | null;
    };
  };

type RenderFont = NonNullable<SocialPostRenderPayload["brandkit"]>["fonts"][number];

const getRenderWindow = (): RenderWindow => window as RenderWindow;

const preloadImage = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(), { once: true });
    image.addEventListener(
      "error",
      () => reject(new Error(`Failed to load render image asset: ${src}`)),
      { once: true },
    );
    image.src = src;
  });

const waitForStylesheet = (link: HTMLLinkElement): Promise<void> =>
  new Promise((resolve, reject) => {
    if (link.dataset.renderFontLoaded === "true") {
      resolve();
      return;
    }

    const timeout = window.setTimeout(() => {
      reject(new Error(`Timed out loading render font stylesheet: ${link.href}`));
    }, 10000);
    link.addEventListener(
      "load",
      () => {
        window.clearTimeout(timeout);
        link.dataset.renderFontLoaded = "true";
        resolve();
      },
      { once: true },
    );
    link.addEventListener(
      "error",
      () => {
        window.clearTimeout(timeout);
        reject(new Error(`Failed to load render font stylesheet: ${link.href}`));
      },
      { once: true },
    );
  });

const loadGoogleFont = async (font: RenderFont) => {
  let link = Array.from(
    document.querySelectorAll<HTMLLinkElement>("link[data-render-font-url]"),
  ).find((candidate) => candidate.dataset.renderFontUrl === font.loadUrl);

  if (!link) {
    link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = font.loadUrl;
    link.dataset.renderFontUrl = font.loadUrl;
    document.head.append(link);
  }

  await waitForStylesheet(link);

  const weight = Number.parseInt(font.selectedVariant, 10);
  await document.fonts.load(
    `${Number.isFinite(weight) ? weight : 400} 16px ${JSON.stringify(font.family)}`,
  );
};

const preloadRenderAssets = async (payload: SocialPostRenderPayload): Promise<void> => {
  const imageUrls = new Set(payload.linkedAssets.map((asset) => asset.signedUrl));
  if (payload.brandkit?.logo?.signedUrl) {
    imageUrls.add(payload.brandkit.logo.signedUrl);
  }

  await Promise.all(Array.from(imageUrls).map((url) => preloadImage(url)));

  for (const font of payload.brandkit?.fonts ?? []) {
    if (font.source === "brand_confirmed") {
      const face = new FontFace(font.family, `url(${font.loadUrl})`);
      const loaded = await face.load();
      document.fonts.add(loaded);
      continue;
    }

    await loadGoogleFont(font);
  }

  await document.fonts.ready;
};

const markRenderError = (message: string): void => {
  const renderWindow = getRenderWindow();
  renderWindow["__runcheetahSocialRenderReady"] = false;
  renderWindow["__runcheetahSocialRenderError"] = message;
};

const markRenderReady = (payload: SocialPostRenderPayload): void => {
  const renderWindow = getRenderWindow();
  renderWindow["__runcheetahSocialRenderResult"] = {
    width: payload.plan.scene.canvas.width,
    height: payload.plan.scene.canvas.height,
    previewSource: payload.previewSource,
    generationTaskId: payload.generationTaskId,
    slideId: payload.slideId,
    slideIndex: payload.slideIndex,
  };
  renderWindow["__runcheetahSocialRenderError"] = undefined;
  renderWindow["__runcheetahSocialRenderReady"] = true;
};

export default function SocialPostRenderPage() {
  const [payload, setPayload] = useState<SocialPostRenderPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const renderWindow = getRenderWindow();
    renderWindow["__runcheetahSocialRenderReady"] = false;
    renderWindow["__runcheetahSocialRenderError"] = undefined;
    renderWindow["__runcheetahSocialRenderResult"] = undefined;

    const controller = new AbortController();

    const load = async () => {
      try {
        const token = new URLSearchParams(window.location.search).get("token");
        if (!token) {
          throw new Error("Missing social post render token");
        }

        const response = await fetch(
          `${import.meta.env.VITE_API_URL ?? ""}/api/public/social-post-render-payload?token=${encodeURIComponent(token)}`,
          { signal: controller.signal },
        );
        if (!response.ok) {
          throw new Error(`Social post render payload request failed: ${response.status}`);
        }

        const parsed = socialPostRenderPayloadSchema.parse(await response.json());
        await preloadRenderAssets(parsed);

        if (!controller.signal.aborted) {
          setPayload(parsed);
        }
      } catch (caught) {
        if (controller.signal.aborted) {
          return;
        }
        const message = caught instanceof Error ? caught.message : String(caught);
        setError(message);
        markRenderError(message);
      }
    };

    void load();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!payload) {
      return;
    }

    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void document.fonts.ready.then(() => {
          if (!cancelled) {
            markRenderReady(payload);
          }
        });
      });
    });

    return () => {
      cancelled = true;
    };
  }, [payload]);

  if (error) {
    return <main className="p-6 text-sm text-red-700">{error}</main>;
  }

  if (!payload) {
    return <main className="min-h-screen bg-white" />;
  }

  return (
    <main className="min-h-screen bg-white">
      <div
        id="social-post-render-target"
        style={{
          width: payload.plan.scene.canvas.width,
          height: payload.plan.scene.canvas.height,
        }}
      >
        <SocialPostPreview
          platform={payload.plan.platform}
          type={payload.plan.postType}
          renderMode="preview"
          plan={payload.plan}
          linkedAssets={payload.linkedAssets}
          brandkit={payload.brandkit}
          showMetadata={false}
          fillContainer="contain"
        />
      </div>
    </main>
  );
}
